import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import apiUrl from "../libs/baseUrl";
import { errorHandeling } from "../errorHandeling";

export default function useDisciplinaryCases({
  page = 1,
  filters = {},
}) {
  const token = useSelector(
    (state) => state.user.token
  );


  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] =
    useState(page);
  const [totalPages, setTotalPages] =
    useState(1);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] = useState(null);

  // ================= BUILD PARAMS =================
  const buildParams = useCallback(
    (pageNumber, filters) => {
      const params = {
        page: pageNumber,
      };

      if (filters.search?.trim()) {
        params.search =
          filters.search.trim();
      }

      if (filters.employee_job_id) {
        params.employee_job_id =
          filters.employee_job_id;
      }

      if (filters.from_date) {
        params.from_date =
          filters.from_date;
      }

      if (filters.to_date) {
        params.to_date =
          filters.to_date;
      }

      if (filters.status) {
        params.status = filters.status;
      }

      // "include" (all) or "only" (archived only); absent => backend excludes archived.
      if (filters.archived) {
        params.archived = filters.archived;
      }

      return params;
    },
    []
  );

  // ================= FETCH FUNCTION =================
  const fetchCases = useCallback(
    async (
      pageNumber = 1,
      activeFilters = {}
    ) => {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${apiUrl}/disciplinary-cases`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
            params: buildParams(
              pageNumber,
              activeFilters
            ),
          }
        );

        if (
          response.data?.status ===
          "success"
        ) {
          const result =
            response.data.data;

          setData(result.data ?? []);
          setCurrentPage(
            result.current_page ?? 1
          );
          setTotalPages(
            result.last_page ?? 1
          );
        } else {
          setData([]);
          setError(
            "فشل في جلب البيانات"
          );
        }
      } catch (err) {
        setData([]);
        setError(errorHandeling(err));
      } finally {
        setLoading(false);
      }
    },
    [token, buildParams]
  );

  // ================= AUTO FETCH =================
  // Key the effect on a stable serialization of the filter VALUES, not the
  // `filters` object reference. A parent that rebuilds the filters object on
  // each render would otherwise retrigger this effect every render → refetch loop.
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    fetchCases(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filtersKey, fetchCases]);

  // Reload the SAME page with the SAME filters. Callers (after a decision, submit,
  // archive, delete…) call it with no arguments, which used to drop back to page 1
  // with no filters while the filter form still showed them.
  const refetch = useCallback(
    () => fetchCases(page, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchCases, page, filtersKey]
  );

  return {
    data,
    currentPage,
    totalPages,
    isLoading: loading,
    isFetching: loading,
    error,
    refetch,
  };
}